import { SYNC_TOTAL_BYTES } from "@classroom/domain";
import { type CipherObject, canonical, MAX_CIPHER_BYTES } from "./cryptoV2";

export type DriveFileV2 = { id: string; size?: string; trashed?: boolean; appProperties?: Record<string, string> };
export type DriveChangeV2 = { fileId: string; removed?: boolean; file?: DriveFileV2 };
export interface SyncDriveV2 {
  allocateId(): Promise<string>;
  read(id: string): Promise<CipherObject | null>;
  create(id: string, cipher: CipherObject, kind: "operation" | "checkpoint" | "chunk"): Promise<void>;
  list(pageToken?: string): Promise<{ files: DriveFileV2[]; nextPageToken?: string }>;
  startChanges(): Promise<string>;
  changes(pageToken: string): Promise<{ changes: DriveChangeV2[]; nextPageToken?: string; newStartPageToken?: string }>;
}
const driveId = (id: string) => {
  if (!/^[\w-]{1,256}$/.test(id)) throw new Error("sync-drive-id");
  return id;
};
export class GoogleDriveV2 implements SyncDriveV2 {
  constructor(
    private readonly accessToken: () => Promise<string>,
    private readonly network: typeof fetch = fetch,
  ) {}
  private async request(url: URL, init: RequestInit = {}, notFound = false): Promise<unknown> {
    const token = await this.accessToken();
    let response: Response;
    try {
      response = await this.network.call(globalThis, url, {
        ...init,
        headers: { ...init.headers, Authorization: `Bearer ${token}` },
        signal: AbortSignal.timeout(15000),
        redirect: "error",
        cache: "no-store",
      });
    } catch {
      throw new Error("sync-drive-request-failed");
    }
    if (notFound && response.status === 404) return null;
    if (response.status === 409) throw new Error("sync-drive-exists");
    if (response.status === 401) throw new Error("sync-google-reconnect");
    if (response.status === 429) throw new Error("sync-rate-limit");
    const reader = response.body?.getReader();
    if (!reader) throw new Error("sync-drive-response-format");
    let total = 0;
    const parts: Uint8Array[] = [];
    try {
      while (true) {
        const p = await reader.read();
        if (p.done) break;
        total += p.value.length;
        if (total > MAX_CIPHER_BYTES) {
          await reader.cancel();
          throw new Error("sync-file-limit");
        }
        parts.push(p.value);
      }
    } finally {
      reader.releaseLock();
    }
    const bytes = new Uint8Array(total);
    let at = 0;
    for (const part of parts) {
      bytes.set(part, at);
      at += part.length;
    }
    let value: unknown;
    try {
      value = JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(bytes));
    } catch {
      throw new Error("sync-drive-response-format");
    }
    if (!response.ok) {
      const reason = (value as { error?: { errors?: { reason?: string }[] } })?.error?.errors?.[0]?.reason;
      if (reason === "storageQuotaExceeded") throw new Error("sync-drive-quota");
      if (["rateLimitExceeded", "userRateLimitExceeded", "dailyLimitExceeded"].includes(reason ?? ""))
        throw new Error("sync-rate-limit");
      if (response.status === 403) throw new Error("sync-drive-denied");
      throw new Error("sync-drive-request-failed");
    }
    return value;
  }
  private url(path: string, params: Record<string, string> = {}) {
    const url = new URL(`https://www.googleapis.com/drive/v3/${path}`);
    url.search = new URLSearchParams(params).toString();
    return url;
  }
  async allocateId() {
    const result = (await this.request(
      this.url("files/generateIds", { count: "1", space: "appDataFolder", type: "files" }),
    )) as { ids?: string[] };
    return driveId(result.ids?.[0] ?? "");
  }
  async read(id: string) {
    return (await this.request(this.url(`files/${driveId(id)}`, { alt: "media" }), {}, true)) as CipherObject | null;
  }
  async create(id: string, cipher: CipherObject, kind: "operation" | "checkpoint" | "chunk") {
    driveId(id);
    const body = canonical(cipher);
    if (new TextEncoder().encode(body).length > MAX_CIPHER_BYTES) throw new Error("sync-file-limit");
    const h = cipher.header;
    const metadata = {
      id,
      name: `classroom-sync-v2-${h.objectId}`,
      parents: ["appDataFolder"],
      mimeType: "application/json",
      appProperties: {
        cdSync: "2",
        dataset: h.datasetId,
        generation: h.generation,
        keyVersion: String(h.keyVersion),
        kind,
        objectId: h.objectId,
      },
    };
    const boundary = `sync_${crypto.randomUUID()}`;
    const multipart = `--${boundary}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n${JSON.stringify(metadata)}\r\n--${boundary}\r\nContent-Type: application/json\r\n\r\n${body}\r\n--${boundary}--\r\n`;
    const url = new URL("https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id");
    const value = (await this.request(url, {
      method: "POST",
      headers: { "Content-Type": `multipart/related; boundary=${boundary}` },
      body: multipart,
    })) as { id?: string };
    if (value.id !== id) throw new Error("sync-drive-id");
  }
  async list(pageToken?: string) {
    return (await this.request(
      this.url("files", {
        spaces: "appDataFolder",
        q: "trashed = false and appProperties has { key='cdSync' and value='2' }",
        pageSize: "1000",
        fields: "nextPageToken,files(id,size,trashed,appProperties)",
        ...(pageToken ? { pageToken } : {}),
      }),
    )) as { files: DriveFileV2[]; nextPageToken?: string };
  }
  async startChanges() {
    const result = (await this.request(this.url("changes/startPageToken", { spaces: "appDataFolder" }))) as {
      startPageToken?: string;
    };
    if (typeof result.startPageToken !== "string" || !result.startPageToken) throw new Error("sync-drive-cursor");
    return result.startPageToken;
  }
  async changes(pageToken: string) {
    return (await this.request(
      this.url("changes", {
        spaces: "appDataFolder",
        pageToken,
        pageSize: "1000",
        includeRemoved: "true",
        fields: "nextPageToken,newStartPageToken,changes(fileId,removed,file(id,size,trashed,appProperties))",
      }),
    )) as { changes: DriveChangeV2[]; nextPageToken?: string; newStartPageToken?: string };
  }
}

export function checkDriveCapacity(files: DriveFileV2[], newBytes = 0) {
  let total = newBytes;
  for (const file of files) {
    if (file.size === undefined || !/^\d+$/.test(file.size)) throw new Error("sync-drive-response-format");
    const size = Number(file.size);
    if (!Number.isSafeInteger(size) || size < 0) throw new Error("sync-drive-response-format");
    total += size;
  }
  if (total > SYNC_TOTAL_BYTES) throw new Error("sync-capacity-limit");
  return total;
}
